// Wizard schemas for the 3 MVP documents. Each schema is a list of steps.
// A step has: id, title, help, fields[]. Field types: text | number | mxn | date |
// textarea | select | radio | checkboxGroup | persona | inmueble | tramos | showIf.

const MONEDA = { name: "moneda", label: "Moneda", type: "select", options: [
  { value: "MXN", label: "Pesos mexicanos (MXN)" }, { value: "USD", label: "Dólares (USD)" },
] };
const NOTA_USD = { name: "nota_moneda_usd", type: "note", showIf: (d) => d.moneda === "USD",
  text: "Las plantillas actuales redactan los montos en pesos. Si el contrato es en dólares, pídele a VALIDACIÓN LEGAL la redacción en USD antes de firmar." };

// Campo del monto que se prellena desde la operación.
export const MONTO_FIELD = {
  comision_mercantil_venta: "precio_venta", carta_oferta_compraventa: "precio_ofertado",
  promesa_compraventa: "precio_venta", arrendamiento_habitacional: "monto_renta_numero",
};

export const documentSchemas = {
  comision_mercantil_venta: {
    label: "Comisión Mercantil (Venta)",
    color: "#1E3A5F",
    initialTitle: "Comisión Mercantil",
    steps: [
      {
        id: "propietario",
        title: "¿Quién es el propietario?",
        help: "Selecciona o registra al propietario del inmueble. Sus datos completos aparecerán en el contrato.",
        fields: [{ name: "_op_propietario_id", label: "Propietario", type: "persona", required: true }],
      },
      {
        id: "inmueble",
        title: "¿Qué inmueble se vende?",
        help: "Elige el inmueble; sus datos oficiales viajarán a todos los documentos de esta operación.",
        fields: [{ name: "_op_inmueble_id", label: "Inmueble", type: "inmueble", required: true }],
      },
      {
        id: "precio",
        title: "¿Cuál es el precio de venta?",
        help: "Precio total al que el propietario desea vender el inmueble. Puedes ajustarlo después.",
        fields: [
          { name: "precio_venta", label: "Precio de venta", type: "mxn", required: true },
          MONEDA, NOTA_USD,
        ],
      },
      {
        id: "comision",
        title: "¿Cuál es la comisión pactada?",
        help: "Ingresa el porcentaje de comisión. Común en México: 3% a 6% más IVA.",
        fields: [{ name: "comision_pct", label: "Comisión (%)", type: "number", step: "0.1", required: true, placeholder: "5" }],
      },
      {
        id: "exclusividad",
        title: "¿El encargo es exclusivo?",
        help: "En exclusividad el propietario canaliza todos los prospectos al asesor y la relación es más protegida.",
        fields: [
          { name: "exclusividad", label: "¿Exclusividad?", type: "radio", options: [
            { value: "si", label: "Sí, exclusiva" },
            { value: "no", label: "No, sin exclusiva" },
          ], required: true },
          { name: "vigencia_meses", label: "Vigencia (meses)", type: "number", placeholder: "6",
            showIf: (d) => d.exclusividad === "si" },
        ],
      },
      {
        id: "proteccion",
        title: "Protección de prospectos",
        help: "Si aceptas, se agrega una 'cola' de protección: si el propietario vende a un prospecto que tú registraste después de terminar el contrato, la comisión se paga igualmente.",
        fields: [
          { name: "registro_prospectos", label: "¿Incluir cola de protección de prospectos?", type: "radio", options: [
            { value: "si", label: "Sí, incluir" }, { value: "no", label: "No" },
          ], required: true },
          { name: "cola_meses", label: "Cola de protección (meses)", type: "number", placeholder: "6",
            showIf: (d) => d.registro_prospectos === "si" },
        ],
      },
      {
        id: "comisionista",
        title: "Tus datos como comisionista",
        help: "Se toman de Mi perfil. Solo elige la cuenta donde recibirás la comisión.",
        fields: [{ name: "cuenta_bancaria_id", label: "Cuenta para recibir la comisión", type: "asesor_perfil", withCuenta: true, required: true }],
      },
      {
        id: "firma",
        title: "Ciudad y fecha de firma",
        help: "Estos datos definen dónde y cuándo se firma el contrato.",
        fields: [
          { name: "ciudad_firma", label: "Ciudad de firma", type: "text", required: true, placeholder: "Ciudad de México" },
          { name: "fecha_firma", label: "Fecha de firma", type: "date", required: true },
          { name: "jurisdiccion", label: "Tribunales competentes (opcional)", type: "text", placeholder: "Ciudad de México" },
        ],
      },
    ],
  },

  carta_oferta_compraventa: {
    label: "Carta Oferta de Compraventa",
    color: "#2A5C55",
    initialTitle: "Carta Oferta de Compraventa",
    steps: [
      {
        id: "propietario",
        title: "¿A quién va dirigida la oferta?",
        help: "El propietario del inmueble es el destinatario de la carta.",
        fields: [{ name: "_op_propietario_id", label: "Propietario destinatario", type: "persona", required: true }],
      },
      {
        id: "inmueble",
        title: "¿Sobre qué inmueble ofertas?",
        fields: [{ name: "_op_inmueble_id", label: "Inmueble", type: "inmueble", required: true }],
      },
      {
        id: "comprador",
        title: "¿Quién ofrece comprar?",
        help: "Registra los datos del comprador (oferente).",
        fields: [{ name: "_op_contraparte_id", label: "Comprador (oferente)", type: "persona", required: true }],
      },
      {
        id: "precio",
        title: "Precio ofertado",
        fields: [
          { name: "precio_ofertado", label: "Precio ofertado", type: "mxn", required: true },
          MONEDA, NOTA_USD,
        ],
      },
      {
        id: "origen",
        title: "¿De dónde vienen los fondos?",
        help: "Puedes combinar recursos propios y crédito hipotecario.",
        fields: [
          { name: "origen_fondos", label: "Origen de fondos", type: "checkboxGroup", options: [
            { value: "recursos_propios", label: "Recursos propios" },
            { value: "credito", label: "Crédito hipotecario/bancario" },
            { value: "credito_infonavit", label: "Crédito INFONAVIT/FOVISSSTE" },
          ], required: true },
          { name: "institucion_credito", label: "Institución del crédito", type: "text",
            showIf: (d) => (d.origen_fondos || []).includes("credito") || (d.origen_fondos || []).includes("credito_infonavit") },
          { name: "monto_credito", label: "Monto del crédito", type: "mxn",
            showIf: (d) => (d.origen_fondos || []).includes("credito") || (d.origen_fondos || []).includes("credito_infonavit") },
        ],
      },
      {
        id: "tramos",
        title: "Esquema de pagos",
        help: "Define uno o más pagos. Puede ser pago único al firmar la escritura o varios pagos.",
        fields: [{ name: "tramos_pago", label: "Pagos", type: "tramos" }],
      },
      {
        id: "plazo",
        title: "Plazo de aceptación",
        help: "Fecha límite para que el propietario acepte esta oferta por escrito.",
        fields: [
          { name: "plazo_aceptacion", label: "Válida hasta", type: "date", required: true },
          { name: "condiciones", label: "Condiciones adicionales (opcional)", type: "textarea" },
        ],
      },
      {
        id: "firma",
        title: "Ciudad y fecha",
        fields: [
          { name: "ciudad_firma", label: "Ciudad", type: "text", required: true, placeholder: "Ciudad de México" },
          { name: "fecha_firma", label: "Fecha de la carta", type: "date", required: true },
        ],
      },
    ],
  },

  promesa_compraventa: {
    label: "Promesa de Compraventa",
    color: "#1F4640",
    initialTitle: "Promesa de Compraventa",
    steps: [
      {
        id: "vendedor",
        title: "Promitente vendedor",
        help: "El sistema aplicará la redacción de persona física o moral según el tipo del contacto.",
        fields: [{ name: "_op_propietario_id", label: "Vendedor principal", type: "persona", required: true }],
      },
      {
        id: "multi_vendedor",
        title: "¿Hay más de un vendedor?",
        help: "Solo aplica si es persona física con copropiedad. Si es persona moral, se asume un solo representante.",
        fields: [
          { name: "hay_vendedor_2", label: "¿Hay un segundo vendedor?", type: "radio", required: true, options: [
            { value: "no", label: "No, un solo vendedor" },
            { value: "si", label: "Sí, hay un segundo vendedor" },
          ]},
          { name: "vendedor_2_id", label: "Segundo vendedor", type: "persona",
            showIf: (d) => d.hay_vendedor_2 === "si" },
        ],
      },
      {
        id: "comprador",
        title: "Promitente comprador",
        fields: [{ name: "_op_contraparte_id", label: "Comprador principal", type: "persona", required: true }],
      },
      {
        id: "multi_comprador",
        title: "¿Hay más de un comprador?",
        fields: [
          { name: "hay_comprador_2", label: "¿Hay un segundo comprador?", type: "radio", required: true, options: [
            { value: "no", label: "No" }, { value: "si", label: "Sí" },
          ]},
          { name: "comprador_2_id", label: "Segundo comprador", type: "persona",
            showIf: (d) => d.hay_comprador_2 === "si" },
        ],
      },
      {
        id: "inmueble",
        title: "Inmueble",
        fields: [{ name: "_op_inmueble_id", label: "Inmueble objeto", type: "inmueble", required: true }],
      },
      {
        id: "inmueble_juridico",
        title: "Datos jurídicos del inmueble",
        help: "Estos datos van en la Declaración Primera-b) y son indispensables para la escritura.",
        fields: [
          { name: "calle_inmueble", label: "Calle", type: "text", required: true },
          { name: "numero_inmueble", label: "Número", type: "text", required: true },
          { name: "colonia_inmueble", label: "Colonia", type: "text", required: true },
          { name: "alcaldia_inmueble", label: "Alcaldía o Municipio", type: "text", required: true },
          { name: "cp_inmueble", label: "Código Postal", type: "text", required: true },
          { name: "ciudad_estado_inmueble", label: "Ciudad, Estado", type: "text", required: true, placeholder: "Ciudad de México, CDMX" },
          { name: "numero_escritura_propiedad", label: "Número de escritura pública de propiedad", type: "text", required: true },
          { name: "fecha_escritura_propiedad", label: "Fecha de la escritura", type: "date", required: true },
          { name: "notario_propiedad", label: "Nombre del notario que dio fe", type: "text", required: true },
          { name: "numero_notaria_propiedad", label: "Número de notaría", type: "text", required: true },
          { name: "ciudad_notaria_propiedad", label: "Ciudad/entidad de la notaría", type: "text", required: true },
          { name: "folio_real", label: "Folio Real del RPP", type: "text", required: true },
        ],
      },
      {
        id: "precio",
        title: "Precio y arras",
        help: "El precio va en número y letras. Las arras suelen ser 10% del precio.",
        fields: [
          { name: "precio_venta", label: "Precio total de la operación", type: "mxn", required: true },
          MONEDA, NOTA_USD,
          { name: "precio_venta_letras", label: "Precio en letras (sin centavos)", type: "text", required: true },
          { name: "arras", label: "Arras (entrega inicial)", type: "mxn", required: true },
          { name: "arras_letras", label: "Arras en letras", type: "text", required: true },
        ],
      },
      {
        id: "banking_vendedor_1",
        title: "Cuenta bancaria del vendedor principal",
        help: "Va en la Cláusula Segunda. El cónyuge NO se agrega aquí — solo comparece como consentimiento.",
        fields: [
          { name: "vendedor_1_cuenta_id", label: "Cuenta bancaria del vendedor", type: "cuenta", personaField: "_op_propietario_id", required: true },
          { name: "vendedor_1_arras", label: "Porción de las arras que le corresponde (MXN)", type: "mxn", required: true },
          { name: "vendedor_1_remanente", label: "Porción del remanente que le corresponde (MXN)", type: "mxn", required: true },
        ],
      },
      {
        id: "banking_vendedor_2",
        title: "Cuenta bancaria del segundo vendedor",
        showIf: (d) => d.hay_vendedor_2 === "si",
        fields: [
          { name: "vendedor_2_cuenta_id", label: "Cuenta bancaria del segundo vendedor", type: "cuenta", personaField: "vendedor_2_id" },
          { name: "vendedor_2_arras", label: "Porción de las arras (MXN)", type: "mxn",
            readOnly: true,
            hint: "Se autocalcula: total de arras − porción del vendedor 1." },
          { name: "vendedor_2_remanente", label: "Porción del remanente (MXN)", type: "mxn",
            readOnly: true,
            hint: "Se autocalcula: remanente − porción del vendedor 1." },
        ],
      },
      {
        id: "pagos_parciales",
        title: "¿Habrá pagos parciales antes de la escrituración?",
        help: "Enganches adicionales entre la firma de la promesa y la escritura. Si no hay, el remanente se numera II; si hay, III.",
        fields: [
          { name: "hay_pagos_parciales", label: "¿Hay pagos parciales?", type: "radio", required: true, options: [
            { value: "no", label: "No, solo arras y remanente" },
            { value: "si", label: "Sí, hay pagos parciales" },
          ]},
          { name: "pagos_parciales_lista", type: "tramos",
            showIf: (d) => d.hay_pagos_parciales === "si", label: "Calendario de pagos parciales" },
          { name: "_nota_parciales_reparto", type: "note",
            showIf: (d) => d.hay_pagos_parciales === "si" && d.hay_vendedor_2 === "si",
            text: "Cada pago parcial se repartirá entre los vendedores en la misma proporción que las arras. No necesitas capturar el desglose por vendedor." },
          { name: "pagos_parciales_letras", label: "Suma total de parciales en letras", type: "text",
            showIf: (d) => d.hay_pagos_parciales === "si" },
        ],
      },
      {
        id: "remanente",
        title: "Forma de pago del saldo remanente",
        fields: [
          { name: "forma_pago_remanente", label: "Forma de pago", type: "radio", required: true, options: [
            { value: "recursos_propios", label: "Recursos propios en su totalidad" },
            { value: "credito_hipotecario", label: "Recursos propios + crédito hipotecario" },
          ]},
          { name: "remanente_letras", label: "Remanente en letras", type: "text", required: true },
        ],
      },
      {
        id: "notaria",
        title: "Notaría de escrituración (Cláusula Tercera)",
        fields: [
          { name: "numero_notaria_escrituracion", label: "Número de notaría", type: "text", required: true },
          { name: "ciudad_notaria_escrituracion", label: "Ciudad/entidad", type: "text", required: true },
          { name: "titular_notaria_escrituracion", label: "Titular de la notaría", type: "text", required: true },
          { name: "direccion_notaria_escrituracion", label: "Dirección completa de la notaría", type: "text", required: true },
          { name: "_nota_gastos", type: "note",
            text: "Los gastos de escrituración corren por cuenta de la parte compradora (estándar comercial)." },
        ],
      },
      {
        id: "comisionista",
        title: "Comisionista / Broker (Cláusula Cuarta)",
        fields: [
          { name: "_asesor_perfil", label: "Tus datos (Mi perfil)", type: "asesor_perfil" },
          { name: "nombre_comisionista", label: "Nombre del comisionista o inmobiliaria (opcional)", type: "text",
            hint: "Si lo dejas vacío se usa el nombre de tu perfil." },
        ],
      },
      {
        id: "pena",
        title: "Pena convencional (Cláusula Novena)",
        help: "Monto que la parte incumplida pagará. Suele igualar las arras.",
        fields: [
          { name: "pena_convencional", label: "Monto de la pena convencional", type: "mxn", required: true },
          { name: "pena_convencional_letras", label: "Monto en letras", type: "text", required: true },
        ],
      },
      {
        id: "plazo",
        title: "Plazo para escriturar (Cláusula Décima)",
        fields: [{ name: "dias_plazo_firma_escritura", label: "Días naturales a partir de la firma", type: "number", required: true, placeholder: "60" }],
      },
      {
        id: "condiciones",
        title: "Condiciones especiales (Cláusula Décima Primera)",
        help: "Ejemplos: sucesión, inquilino a desocupar, hipoteca por cancelar. Deja en blanco si no hay ninguna.",
        fields: [
          { name: "condiciones_especiales", label: "Condiciones especiales (texto libre)", type: "textarea" },
        ],
      },
      {
        id: "firma",
        title: "Firma (Cláusula Décima Segunda)",
        fields: [
          { name: "tipo_firma", label: "Modalidad de firma", type: "radio", required: true, options: [
            { value: "fisica", label: "Firma física (presencial, por triplicado)" },
            { value: "electronica", label: "Firma electrónica (Art. 1803 CCDMX)" },
          ]},
          { name: "fecha_firma", label: "Fecha de firma", type: "date", required: true },
          { name: "lugar_de_firma", label: "Lugar de firma", type: "text", required: true, placeholder: "Ciudad de México" },
        ],
      },
    ],
  },

  arrendamiento_habitacional: {
    label: "Arrendamiento Habitacional",
    color: "#7C3F2D",
    initialTitle: "Contrato de Arrendamiento",
    steps: [
      {
        id: "tipo_operacion",
        title: "¿Es un inquilino nuevo o una renovación?",
        help: "Si es renovación, el contrato incluye un bloque de antecedentes que previene la prescripción positiva.",
        fields: [
          { name: "es_renovacion", label: "Tipo de operación", type: "radio", required: true, options: [
            { value: "no", label: "Inquilino nuevo" },
            { value: "si", label: "Renovación de inquilino actual" },
          ]},
          { name: "fecha_inicio_ocupacion_previa", label: "Inicio de la ocupación previa", type: "date",
            showIf: (d) => d.es_renovacion === "si" },
          { name: "fecha_fin_contrato_anterior", label: "Fin del contrato anterior", type: "date",
            showIf: (d) => d.es_renovacion === "si" },
          { name: "fecha_inicio_nuevo_contrato", label: "Inicio del nuevo contrato", type: "date",
            showIf: (d) => d.es_renovacion === "si" },
        ],
      },
      {
        id: "arrendador",
        title: "¿Quién es el arrendador?",
        help: "Selecciona al propietario. Su género y tipo de persona (física/moral) determinan si el contrato usa 'LA ARRENDADORA' o 'EL ARRENDADOR'.",
        fields: [{ name: "_op_propietario_id", label: "Arrendador", type: "persona", required: true }],
      },
      {
        id: "arrendatario",
        title: "¿Quién es el arrendatario?",
        help: "Su género y tipo de persona también determinan la concordancia: 'EL ARRENDATARIO' vs 'LA ARRENDATARIA'.",
        fields: [{ name: "_op_contraparte_id", label: "Arrendatario (inquilino)", type: "persona", required: true }],
      },
      {
        id: "inmueble",
        title: "¿Qué inmueble se arrienda?",
        fields: [
          { name: "_op_inmueble_id", label: "Inmueble", type: "inmueble", required: true },
          { name: "tipo_inmueble", label: "Tipo de inmueble", type: "select", required: true, options: [
            { value: "casa", label: "Casa" },
            { value: "departamento", label: "Departamento" },
            { value: "local", label: "Local" },
            { value: "oficina", label: "Oficina" },
            { value: "bodega", label: "Bodega" },
            { value: "predio", label: "Predio" },
            { value: "terreno", label: "Terreno" },
          ]},
        ],
      },
      {
        id: "vigencia",
        title: "Vigencia del contrato",
        fields: [
          { name: "meses_vigencia", label: "Meses de vigencia forzosa", type: "number", required: true, placeholder: "12" },
          { name: "fecha_inicio_vigencia", label: "Inicio de vigencia", type: "date", required: true },
          { name: "fecha_fin_vigencia", label: "Fin de vigencia", type: "date", required: true },
          { name: "dias_aviso_renovacion", label: "Días de anticipación para avisar renovación", type: "number", placeholder: "30" },
        ],
      },
      {
        id: "renta",
        title: "Renta mensual",
        help: "Escribe el monto en número y en letras para evitar disputas de interpretación.",
        fields: [
          { name: "monto_renta_numero", label: "Monto de la renta", type: "mxn", required: true },
          MONEDA, NOTA_USD,
          { name: "monto_renta_letras", label: "Monto en letras (sin centavos)", type: "text", required: true, placeholder: "quince mil" },
          { name: "centavos_renta", label: "Centavos", type: "text", placeholder: "00" },
          { name: "dias_para_pago", label: "Días naturales del mes para pagar", type: "number", placeholder: "5", required: true },
        ],
      },
      {
        id: "forma_pago",
        title: "Forma de pago",
        fields: [
          { name: "forma_de_pago", label: "Forma de pago", type: "radio", required: true, options: [
            { value: "transferencia", label: "Transferencia electrónica" },
            { value: "cheque", label: "Cheque" },
            { value: "efectivo", label: "Efectivo contra recibo" },
          ]},
          { name: "cuenta_renta_id", label: "Cuenta del arrendador donde se paga la renta", type: "cuenta",
            personaField: "_op_propietario_id", required: true,
            showIf: (d) => d.forma_de_pago === "transferencia" },
        ],
      },
      {
        id: "mantenimiento",
        title: "Cuota de mantenimiento",
        help: "Solo si el inmueble está en un condominio o régimen con cuota fija.",
        fields: [
          { name: "incluir_mantenimiento", label: "¿Incluir cláusula de mantenimiento?", type: "radio", required: true, options: [
            { value: "no", label: "No aplica" },
            { value: "si", label: "Sí, incluir" },
          ]},
          { name: "monto_mantenimiento_numero", label: "Monto de mantenimiento", type: "mxn",
            showIf: (d) => d.incluir_mantenimiento === "si" },
          { name: "monto_mantenimiento_letras", label: "Monto en letras", type: "text",
            showIf: (d) => d.incluir_mantenimiento === "si" },
          { name: "centavos_mantenimiento", label: "Centavos", type: "text", placeholder: "00",
            showIf: (d) => d.incluir_mantenimiento === "si" },
          { name: "banco_mantenimiento", label: "Banco", type: "text",
            showIf: (d) => d.incluir_mantenimiento === "si" },
          { name: "beneficiario_mantenimiento", label: "Beneficiario", type: "text",
            showIf: (d) => d.incluir_mantenimiento === "si" },
          { name: "clabe_mantenimiento", label: "CLABE", type: "text",
            showIf: (d) => d.incluir_mantenimiento === "si" },
        ],
      },
      {
        id: "deposito_penas",
        title: "Depósito en garantía y penas",
        fields: [
          { name: "monto_deposito_numero", label: "Depósito en garantía", type: "mxn", required: true },
          { name: "monto_deposito_letras", label: "Depósito en letras", type: "text", required: true },
          { name: "centavos_deposito", label: "Centavos", type: "text", placeholder: "00" },
          { name: "tasa_mora_diaria", label: "Tasa de mora diaria (%)", type: "number", placeholder: "3" },
          { name: "tasa_mora_maxima", label: "Tope mensual de mora (%)", type: "number", placeholder: "10" },
          { name: "cantidad_meses_pena", label: "Meses de renta como pena convencional", type: "number", placeholder: "2" },
          { name: "dias_desocupacion_rescision", label: "Días para desocupar en rescisión", type: "number", placeholder: "5" },
          { name: "dias_reporte_vicios", label: "Días para reportar vicios ocultos", type: "number", placeholder: "15" },
          { name: "dias_reparacion_vicios", label: "Días máx. para repararlos", type: "number", placeholder: "30" },
        ],
      },
      {
        id: "ajuste",
        title: "Ajuste anual de renta",
        help: "En México lo habitual es ajustar por INPC cada 12 meses.",
        fields: [
          { name: "meses_ajuste_renta", label: "Cada cuántos meses se ajusta", type: "number", placeholder: "12", required: true },
          { name: "indice_ajuste", label: "Índice de ajuste", type: "radio", required: true, options: [
            { value: "inpc", label: "INPC (Índice Nacional de Precios al Consumidor)" },
            { value: "porcentaje_fijo", label: "Porcentaje fijo pactado" },
          ]},
          { name: "porcentaje_ajuste_fijo", label: "Porcentaje fijo (%)", type: "number",
            showIf: (d) => d.indice_ajuste === "porcentaje_fijo" },
        ],
      },
      {
        id: "mascotas",
        title: "Mascotas",
        fields: [
          { name: "clausula_mascotas", label: "¿Se permiten mascotas?", type: "radio", required: true, options: [
            { value: "prohibido", label: "Prohibido" },
            { value: "permitido", label: "Permitido con condiciones" },
          ]},
          { name: "cantidad_mascotas", label: "Cantidad autorizada", type: "number",
            showIf: (d) => d.clausula_mascotas === "permitido" },
          { name: "tipo_mascotas", label: "Tipo de mascotas (ej. perro pequeño, gato)", type: "text",
            showIf: (d) => d.clausula_mascotas === "permitido" },
        ],
      },
      {
        id: "estacionamiento",
        title: "Estacionamiento",
        fields: [
          { name: "clausula_estacionamiento", label: "¿Incluye estacionamiento?", type: "radio", required: true, options: [
            { value: "sin", label: "No incluye" },
            { value: "con", label: "Sí, incluye" },
          ]},
          { name: "numero_cajones", label: "Número de cajones", type: "number",
            showIf: (d) => d.clausula_estacionamiento === "con" },
          { name: "ubicacion_cajones", label: "Ubicación de los cajones", type: "text",
            showIf: (d) => d.clausula_estacionamiento === "con" },
        ],
      },
      {
        id: "fiador",
        title: "Fiador / Obligado solidario",
        help: "Opcional. Si el inquilino tiene fiador, se agregan las declaraciones y la cláusula específicas.",
        fields: [
          { name: "hay_fiador", label: "¿Hay fiador u obligado solidario?", type: "radio", required: true, options: [
            { value: "no", label: "No" },
            { value: "si", label: "Sí" },
          ]},
          { name: "nombre_fiador", label: "Nombre completo del fiador", type: "text",
            showIf: (d) => d.hay_fiador === "si" },
          { name: "tipo_persona_fiador", label: "Tipo de persona", type: "select", options: [
            { value: "fisica", label: "Persona física" },
            { value: "moral", label: "Persona moral" },
          ], showIf: (d) => d.hay_fiador === "si" },
          { name: "curp_fiador", label: "CURP", type: "text",
            showIf: (d) => d.hay_fiador === "si" && d.tipo_persona_fiador === "fisica" },
          { name: "clave_elector_fiador", label: "Clave de Elector", type: "text",
            showIf: (d) => d.hay_fiador === "si" && d.tipo_persona_fiador === "fisica" },
          { name: "domicilio_legal_fiador", label: "Domicilio", type: "text",
            showIf: (d) => d.hay_fiador === "si" },
          { name: "whatsapp_fiador", label: "WhatsApp / Teléfono", type: "text",
            showIf: (d) => d.hay_fiador === "si" },
          { name: "email_fiador", label: "Correo electrónico", type: "text",
            showIf: (d) => d.hay_fiador === "si" },
          { name: "direccion_inmueble_garantia", label: "Dirección del inmueble en garantía", type: "text",
            showIf: (d) => d.hay_fiador === "si" },
        ],
      },
      {
        id: "firma",
        title: "Firma y jurisdicción",
        fields: [
          { name: "tipo_firma", label: "Modalidad de firma", type: "radio", required: true, options: [
            { value: "fisica", label: "Firma física (presencial)" },
            { value: "electronica", label: "Firma electrónica (NOM 151)" },
          ]},
          { name: "fecha_firma", label: "Fecha de firma", type: "date", required: true },
          { name: "lugar_de_firma", label: "Lugar de firma", type: "text", required: true, placeholder: "Ciudad de México",
            showIf: (d) => d.tipo_firma === "fisica" },
          { name: "ciudad_de_jurisdiccion", label: "Ciudad de jurisdicción", type: "text", required: true, placeholder: "Ciudad de México" },
        ],
      },
    ],
  },
};

export const tipoDocOptions = [
  { value: "comision_mercantil_venta", ...documentSchemas.comision_mercantil_venta },
  { value: "carta_oferta_compraventa", ...documentSchemas.carta_oferta_compraventa },
  { value: "promesa_compraventa", ...documentSchemas.promesa_compraventa },
  { value: "arrendamiento_habitacional", ...documentSchemas.arrendamiento_habitacional },
];
